// src/components/PaymentProofModal.js

import React, { useState } from 'react';
import { useContract } from '../hooks/useContract';
import './PaymentProofModal.css';

const PaymentProofModal = ({ trade, isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [transferInfo, setTransferInfo] = useState({
    transferTime: '',
    senderName: '',
    amount: trade?.krwAmount || '',
    bankName: '',
    accountNumber: '',
    memo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: 입력, 2: 확인, 3: 제출
  const { submitPaymentProof, loading, error } = useContract();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // 파일 크기 체크 (10MB 제한)
      if (selectedFile.size > 10 * 1024 * 1024) {
        alert('파일 크기는 10MB 이하로 선택해주세요.');
        return;
      }
      
      // 파일 형식 체크
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        alert('JPG, PNG, PDF 파일만 업로드 가능합니다.');
        return;
      }
      
      setFile(selectedFile);

      // 이미지 미리보기 생성
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTransferInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateStep1 = () => {
    if (!file) {
      alert('송금 증명 파일을 선택해주세요.');
      return false;
    }

    if (!transferInfo.transferTime) {
      alert('송금 시간을 입력해주세요.');
      return false;
    }

    if (!transferInfo.senderName.trim()) {
      alert('입금자명을 입력해주세요.');
      return false;
    }

    if (!transferInfo.bankName.trim()) {
      alert('은행명을 입력해주세요.');
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setCurrentStep(3);

    try {
      // 파일과 함께 송금 증명 제출
      await submitPaymentProof(trade.tradeId, file);
      
      // 추가 정보 로컬 스토리지에 저장
      localStorage.setItem(`transfer_info_${trade.tradeId}`, JSON.stringify(transferInfo));
      
      alert('송금 증명이 성공적으로 제출되었습니다!\n\n이제 판매자가 입금을 확인할 때까지 기다려주세요.');
      onSuccess();
      handleClose();
      
    } catch (error) {
      console.error('송금 증명 제출 실패:', error);
      alert('송금 증명 제출에 실패했습니다: ' + error.message);
      setCurrentStep(2); // 다시 확인 단계로
    } finally {
      setIsSubmitting(false);
    }
  };

  // 테스트용 간단한 제출 (개발/테스트 환경용)
  const handleQuickTest = async () => {
    if (!trade) return;
    
    setIsSubmitting(true);
    try {
      // 테스트용 더미 해시
      const dummyHash = `test_${Date.now()}_${trade.tradeId}`;
      
      const { submitPaymentProof: submitPaymentProofContract } = await import('../utils/contract');
      await submitPaymentProofContract(trade.tradeId, dummyHash);
      
      alert('테스트 송금 증명이 제출되었습니다!');
      onSuccess();
      handleClose();
      
    } catch (error) {
      console.error('테스트 제출 실패:', error);
      alert('테스트 제출 실패: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setFilePreview(null);
    setTransferInfo({
      transferTime: '',
      senderName: '',
      amount: trade?.krwAmount || '',
      bankName: '',
      accountNumber: '',
      memo: ''
    });
    setCurrentStep(1);
    setIsSubmitting(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen || !trade) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content payment-proof-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>송금 증명 제출</h3>
          <div className="step-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
          </div>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">
          {/* 거래 요약 정보 */}
          <div className="trade-summary-box">
            <h4>📋 거래 정보</h4>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">거래 ID:</span>
                <span className="value">#{trade.tradeId}</span>
              </div>
              <div className="summary-item">
                <span className="label">송금할 금액:</span>
                <span className="value highlight">{parseInt(trade.krwAmount).toLocaleString()} 원</span>
              </div>
              <div className="summary-item">
                <span className="label">받을 WLC:</span>
                <span className="value">{trade.wlcAmount} WLC</span>
              </div>
              <div className="summary-item">
                <span className="label">판매자:</span>
                <span className="value address">{trade.seller?.slice(0, 6)}...{trade.seller?.slice(-4)}</span>
              </div>
            </div>
          </div>

          {/* 1단계: 정보 입력 */}
          {currentStep === 1 && (
            <div className="step-content">
              <h4>📤 1단계: 송금 증명 정보 입력</h4>
              
              <div className="instruction-box">
                <h5>🔔 송금 전 확인사항</h5>
                <ul>
                  <li>판매자에게 계좌번호를 받으셨나요?</li>
                  <li>정확한 금액 <strong>{parseInt(trade.krwAmount).toLocaleString()}원</strong>을 송금하셨나요?</li>
                  <li>입금자명이 본인 이름과 일치하나요?</li>
                </ul>
              </div>

              <div className="form-group">
                <label htmlFor="proofFile" className="required">📎 송금 증명 파일</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="proofFile"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    className="file-input"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="proofFile" className="file-upload-button">
                    {file ? '✅ 파일 선택됨' : '📁 파일 선택하기'}
                  </label>
                  {file && (
                    <div className="file-info">
                      <div className="file-details">
                        <span className="file-name">📄 {file.name}</span>
                        <span className="file-size">({formatFileSize(file.size)})</span>
                      </div>
                      {filePreview && (
                        <div className="file-preview">
                          <img src={filePreview} alt="미리보기" style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px' }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <small>💡 은행 이체 확인증, 모바일 뱅킹 스크린샷 등 (JPG, PNG, PDF, 최대 10MB)</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="transferTime" className="required">⏰ 송금 시간</label>
                  <input
                    type="datetime-local"
                    id="transferTime"
                    name="transferTime"
                    value={transferInfo.transferTime}
                    onChange={handleInputChange}
                    className="form-input"
                    max={new Date().toISOString().slice(0, 16)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="senderName" className="required">👤 입금자명</label>
                  <input
                    type="text"
                    id="senderName"
                    name="senderName"
                    value={transferInfo.senderName}
                    onChange={handleInputChange}
                    placeholder="홍길동"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bankName" className="required">🏦 은행명</label>
                  <input
                    type="text"
                    id="bankName"
                    name="bankName"
                    value={transferInfo.bankName}
                    onChange={handleInputChange}
                    placeholder="예: 국민은행"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="accountNumber">💳 수취인 계좌번호</label>
                  <input
                    type="text"
                    id="accountNumber"
                    name="accountNumber"
                    value={transferInfo.accountNumber}
                    onChange={handleInputChange}
                    placeholder="123-456-789012"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="amount">💰 송금 금액 확인</label>
                <input
                  type="text"
                  id="amount"
                  name="amount"
                  value={parseInt(trade.krwAmount).toLocaleString()} 원
                  className="form-input amount-input"
                  readOnly
                />
              </div>

              <div className="form-group">
                <label htmlFor="memo">📝 추가 메모 (선택사항)</label>
                <textarea
                  id="memo"
                  name="memo"
                  value={transferInfo.memo}
                  onChange={handleInputChange}
                  placeholder="특이사항이나 추가 설명이 있다면 입력해주세요"
                  className="form-textarea"
                  rows="3"
                />
              </div>

              <div className="step-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleClose}
                >
                  취소
                </button>
                
                {/* 개발/테스트용 버튼 */}
                {process.env.NODE_ENV === 'development' && (
                  <button
                    type="button"
                    className="btn-test"
                    onClick={handleQuickTest}
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#28a745', color: 'white' }}
                  >
                    🧪 테스트 제출
                  </button>
                )}
                
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleNext}
                >
                  다음 단계 →
                </button>
              </div>
            </div>
          )}

          {/* 2단계: 확인 */}
          {currentStep === 2 && (
            <div className="step-content">
              <h4>✅ 2단계: 정보 확인</h4>
              
              <div className="confirmation-box">
                <h5>📋 제출할 정보를 확인해주세요</h5>
                
                <div className="confirm-section">
                  <h6>📎 첨부 파일</h6>
                  <div className="file-confirm">
                    <span>📄 {file?.name}</span>
                    <span>({formatFileSize(file?.size || 0)})</span>
                  </div>
                  {filePreview && (
                    <div className="preview-image">
                      <img src={filePreview} alt="첨부 파일 미리보기" style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '8px' }} />
                    </div>
                  )}
                </div>

                <div className="confirm-section">
                  <h6>📝 송금 정보</h6>
                  <div className="confirm-grid">
                    <div className="confirm-item">
                      <span>⏰ 송금 시간:</span>
                      <span>{new Date(transferInfo.transferTime).toLocaleString()}</span>
                    </div>
                    <div className="confirm-item">
                      <span>👤 입금자명:</span>
                      <span>{transferInfo.senderName}</span>
                    </div>
                    <div className="confirm-item">
                      <span>🏦 은행명:</span>
                      <span>{transferInfo.bankName}</span>
                    </div>
                    <div className="confirm-item">
                      <span>💰 송금 금액:</span>
                      <span className="highlight">{parseInt(trade.krwAmount).toLocaleString()} 원</span>
                    </div>
                    {transferInfo.accountNumber && (
                      <div className="confirm-item">
                        <span>💳 수취인 계좌:</span>
                        <span>{transferInfo.accountNumber}</span>
                      </div>
                    )}
                    {transferInfo.memo && (
                      <div className="confirm-item">
                        <span>📝 메모:</span>
                        <span>{transferInfo.memo}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="warning-box">
                <h5>⚠️ 주의사항</h5>
                <ul>
                  <li>제출한 정보는 블록체인에 영구적으로 기록됩니다</li>
                  <li>허위 정보 제출 시 거래가 취소될 수 있습니다</li>
                  <li>정확한 정보인지 다시 한 번 확인해주세요</li>
                </ul>
              </div>

              {error && (
                <div className="error-message">
                  ❌ {error}
                </div>
              )}

              <div className="step-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setCurrentStep(1)}
                  disabled={isSubmitting}
                >
                  ← 이전
                </button>
                
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '제출 중...' : '📤 송금 증명 제출'}
                </button>
              </div>
            </div>
          )}

          {/* 3단계: 제출 중 */}
          {currentStep === 3 && (
            <div className="step-content">
              <div className="loading-container">
                <div className="loading-spinner large"></div>
                <h4>📤 송금 증명을 제출하는 중입니다...</h4>
                <p>트랜잭션을 블록체인에 기록하고 있습니다. 잠시만 기다려주세요.</p>
                
                <div className="progress-steps">
                  <div className="progress-step">✅ 파일 해시 계산 완료</div>
                  <div className="progress-step">⏳ 블록체인에 기록 중...</div>
                  <div className="progress-step pending">⏳ 트랜잭션 확인 대기</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 도움말 */}
        {currentStep === 1 && (
          <div className="modal-footer">
            <div className="help-section">
              <h5>❓ 도움말</h5>
              <details>
                <summary>송금 증명 파일은 어떤 것을 올려야 하나요?</summary>
                <p>• 인터넷뱅킹/모바일뱅킹의 이체 완료 화면 스크린샷<br/>
                • 은행에서 발급한 이체 확인증<br/>
                • ATM 이체 영수증 사진<br/>
                • 송금 시간, 금액, 수취인 정보가 명확히 보여야 합니다</p>
              </details>
              <details>
                <summary>송금 후 얼마나 기다려야 하나요?</summary>
                <p>송금 증명을 제출하면 판매자에게 알림이 전송됩니다. 일반적으로 몇 시간 내에 판매자가 입금을 확인하고 거래를 승인합니다. 만약 24시간 이상 응답이 없다면 고객센터로 문의해주세요.</p>
              </details>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentProofModal;